import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// material
import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  TextField,
  Autocomplete,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  computeHierarchyMeta,
  findOptionByNameOrId,
  parseValidationErrorMessage
} from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AddNewZone } from '../../../network/User';
import { SnackBarAlert } from '../../HelperComponents';

// ----------------------------------------------------------------------

const style = {
  width: '100%',
  maxWidth: { sm: '90%', xs: '90%', md: 800 },
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '30%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const ZoneSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  organizationId: Yup.string().required('Required')
});

AddZoneModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  headOfficesList: PropTypes.array
};

export default function AddZoneModal({ opneAssignedModal, handleClose, headOfficesList }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter based on name match
  const filteredOffices = headOfficesList?.filter((office) =>
    office.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const { sessionUserData, accountDetails } = useAuth();

  const sessionUserPath = accountDetails?.path;

  const { isAdminUser, hierarchyInfo, isHeadOfficeLocked } = useMemo(
    () => computeHierarchyMeta(sessionUserPath, sessionUserData?.user?.role),
    [sessionUserPath, sessionUserData?.user?.role]
  );

  //   address?: string;
  //   city?: string;
  //   state?: string;
  //   pincode?: string;
  //   contactPerson?: string;
  //   phone?: string;
  //   email?: string; add these they are optional
  const addZoneFormik = useFormik({
    initialValues: {
      name: '',
      organizationId: sessionUserData?.user?.organizationId,
      headOfficeName: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      contactPerson: '',
      phone: '',
      email: '',
      headOfficeId: ''
    },
    validationSchema: ZoneSchema,
    onSubmit: () => {
      const payload = Object.fromEntries(
        Object.entries({
          name: values?.name,
          organizationId: values?.organizationId,
          address: values?.address,
          city: values?.city,
          state: values?.state,
          pincode: values?.pincode,
          contactPerson: values?.contactPerson,
          phone: values?.phone,
          email: values?.email,
          headOfficeId: values?.headOfficeId
        }).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );

      AddNewZone(payload)
        .then((res) => {
          addZoneFormik.setSubmitting(false);
          handleClose();
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: `${addZoneFormik?.values?.name} successfully created !`,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);
          addZoneFormik.resetForm();
        })
        .catch((error) => {
          addZoneFormik.setSubmitting(false);
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: parseValidationErrorMessage(error),
            severity: 'error'
          }));
          setIsSnackBarAlertOpen(true);
          addZoneFormik.resetForm();
        });
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue } =
    addZoneFormik;

  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isAdminUser) return;

    if (hierarchyInfo?.headOffice && !values?.headOfficeName) {
      setFieldValue('headOfficeName', hierarchyInfo.headOffice);
    }
  }, [opneAssignedModal, isAdminUser, hierarchyInfo?.headOffice]);

  // Map Head Office name/id -> headOfficeId (required for HO/ZO/RO admins)
  useEffect(() => {
    if (!opneAssignedModal) return;
    if (!isHeadOfficeLocked) return;
    if (values?.headOfficeId) return;

    const desired = hierarchyInfo?.headOffice || values?.headOfficeName;
    const match = findOptionByNameOrId(headOfficesList, desired);
    if (!match) return;

    setFieldValue('headOfficeId', match.id);
    setFieldValue('headOfficeName', match.name || desired || '');
  }, [
    opneAssignedModal,
    isHeadOfficeLocked,
    hierarchyInfo?.headOffice,
    headOfficesList,
    values?.headOfficeId,
    values?.headOfficeName
  ]);

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <FormikProvider enableReinitialize="true" value={addZoneFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                  Create New Zone
                </Typography>
                <IconButton
                  onClick={() => {
                    handleClose();
                    addZoneFormik?.resetForm();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Grid container spacing={2} sx={{ my: 1 }}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Zone Name *"
                      id="zoneName"
                      name="zoneName"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('name')}
                      error={Boolean(touched?.name && errors?.name)}
                      helperText={touched?.name && errors?.name}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  {isHeadOfficeLocked ? (
                    <TextField
                      label="Head Office *"
                      value={values?.headOfficeName || ''}
                      disabled
                      error={Boolean(touched?.headOfficeName && errors?.headOfficeName)}
                      helperText={touched?.headOfficeName && errors?.headOfficeName}
                      inputProps={{ 'aria-label': 'Head office' }}
                      fullWidth
                    />
                  ) : (
                    <Autocomplete
                      options={headOfficesList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={headOfficesList.find((o) => o.id === values.headOfficeId) || null}
                      onChange={(e, newValue) => {
                        setFieldValue('headOfficeId', newValue ? newValue.id : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Head Office"
                          error={Boolean(touched?.headOfficeId && errors?.headOfficeId)}
                          helperText={touched?.headOfficeId && errors?.headOfficeId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Select Head office'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Pincode"
                      id="pincode"
                      name="pincode"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('pincode')}
                      error={Boolean(touched?.pincode && errors?.pincode)}
                      helperText={touched?.pincode && errors?.pincode}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="City"
                      id="city"
                      name="city"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('city')}
                      error={Boolean(touched?.city && errors?.city)}
                      helperText={touched?.city && errors?.city}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="State"
                      id="state"
                      name="state"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('state')}
                      error={Boolean(touched?.state && errors?.state)}
                      helperText={touched?.state && errors?.state}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('address')}
                      error={Boolean(touched?.address && errors?.address)}
                      helperText={touched?.address && errors?.address}
                      multiline
                      rows={2}
                      maxRows={4}
                      label="Address"
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Contact Person Name"
                      id="contactPerson"
                      name="contactPerson"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('contactPerson')}
                      error={Boolean(touched?.contactPerson && errors?.contactPerson)}
                      helperText={touched?.contactPerson && errors?.contactPerson}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      id="email"
                      name="email"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('email')}
                      error={Boolean(touched?.email && errors?.email)}
                      helperText={touched?.email && errors?.email}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Phone"
                      id="phone"
                      name="phone"
                      autoComplete="off"
                      variant="outlined"
                      {...getFieldProps('phone')}
                      error={Boolean(touched?.phone && errors?.phone)}
                      helperText={touched?.phone && errors?.phone}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={addZoneFormik.isSubmitting}
                  sx={{ mt: 2, height: '40px', borderRadius: 2 }}
                  variant="contained"
                  type="submit"
                  size="large"
                >
                  Create
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>
      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
